/**
 * Utillity class for access checks.
 *
 * File: \src\components\login\AccessChecker.ts
 * Created Date: Saturday, May 2nd 2020, 13:12:40
 * Author: Sascha Herrmann
 * -----
 * Last Modified: November 22nd 2024, 07:54:46
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2020 Liebherr-Hausgeräte GmbH
 */

import { AccessCheckResponseRoleEnum, AccessCheckResponse, SupportUserUserAccessLevelsEnum } from '@/api/models';

/**
 * Simple access checker. Moved into its own class to gain flexibilty.
 *
 * @export
 * @class AccessRoleChecker
 */
export default class AccessChecker {
  /**
   * These are the roles that are supposed to have access to the application.
   *
   * @private
   * @static
   * @type {AccessCheckResponseRoleEnum[]}
   * @memberof AccessChecker
   */
  public static APPLICATION_ACCESS_ALLOWED_ROLES: SupportUserUserAccessLevelsEnum[] = [
    SupportUserUserAccessLevelsEnum.DEVELOPER,
    SupportUserUserAccessLevelsEnum.BUSINESS,
    SupportUserUserAccessLevelsEnum.SALES,
    SupportUserUserAccessLevelsEnum.SUPPORT
  ];

  /**
   * Roles for the Internal area. DEVELOPER and BUSINESS.
   *
   * @private
   * @static
   * @type {SupportUserUserAccessLevelsEnum[]}
   * @memberof AccessChecker
   */
  public static INTERNAL_ACCESS_ALLOWED_ROLES: SupportUserUserAccessLevelsEnum[] = [
    SupportUserUserAccessLevelsEnum.DEVELOPER,
    SupportUserUserAccessLevelsEnum.BUSINESS
  ];

  /**
   * Roles for the Support area. DEVELOPER and SUPPORT.
   *
   * @private
   * @static
   * @type {SupportUserUserAccessLevelsEnum[]}
   * @memberof AccessChecker
   */
  public static SUPPORT_CENTER_ACCESS_ALLOWED_ROLES: SupportUserUserAccessLevelsEnum[] = [
    SupportUserUserAccessLevelsEnum.DEVELOPER,
    SupportUserUserAccessLevelsEnum.SUPPORT
  ];

  /**
   * Returns true if there are accessLevels and if one of the accessLevels is in APPLICATION_ACCESS_ALLOWED_ROLES.
   *
   * @static
   *
   * @param {AccessCheckResponse} response The accessLevels to check.
   * @returns {boolean} true if access with current accessLevel is allowed.
   *
   * @memberof AccessChecker
   */
  public static isApplicationAccessAllowed(response: AccessCheckResponse): boolean {
    return (
      response &&
      response.accessLevels &&
      this.APPLICATION_ACCESS_ALLOWED_ROLES.some(r => response.accessLevels.includes(r.toString()))
    );
  }

  /**
   * Returns true if the user is allowed to access internal things. He has roles BUSINESS or DEVELOPER.
   *
   * @static
   *
   * @param {string[]} accessLevels The accessLevels to check.
   * @returns {boolean} true if access with current accessLevel is allowed.
   *
   * @memberof AccessChecker
   */
  public static isInternalAccessAllowed(accessLevels: string[]): boolean {
    return accessLevels && this.INTERNAL_ACCESS_ALLOWED_ROLES.some(r => accessLevels.includes(r.toString()));
  }

  /**
   * Returns true if the user is allowed to access support things. He has roles SUPPORT or DEVELOPER.
   *
   * @static
   * @param {string[]} accessLevels The accessLevels to check.
   * @returns {boolean}
   * @memberof AccessChecker
   */
  public static isSupportCenterAccessAllowed(accessLevels: string[]): boolean {
    return accessLevels && this.SUPPORT_CENTER_ACCESS_ALLOWED_ROLES.some(r => accessLevels.includes(r.toString()));
  }

  /**
   * Does accessLevels contain Developer role?
   *
   * @static
   * @param {string[]} accessLevels of the user to check
   * @returns {boolean}
   * @memberof AccessChecker
   */
  public static isDeveloper(accessLevels: string[]): boolean {
    return accessLevels.includes(SupportUserUserAccessLevelsEnum.DEVELOPER.toString());
  }

  /**
   * Does accessLevels contain Support role?
   *
   * @static
   * @param {string[]} accessLevels of the user to check
   * @returns {boolean}
   * @memberof AccessChecker
   */
  public static isSupportUser(accessLevels: string[]): boolean {
    return accessLevels.includes(SupportUserUserAccessLevelsEnum.SUPPORT.toString());
  }

  /**
   * Does accessLevels contain Business role?
   *
   * @static
   * @param {string[]} accessLevels of the user to check
   * @returns {boolean}
   * @memberof AccessChecker
   */
  public static isBusinessUser(accessLevels: string[]): boolean {
    return accessLevels.includes(SupportUserUserAccessLevelsEnum.BUSINESS.toString());
  }

  /**
   * Does accessLevels contain Sales role?
   *
   * @static
   * @param {string[]} accessLevels of the user to check
   * @returns {boolean}
   * @memberof AccessChecker
   */
  public static isSalesUser(accessLevels: string[]): boolean {
    return accessLevels.includes(SupportUserUserAccessLevelsEnum.SALES.toString());
  }

  /**
   * Is the role a temp admin role?
   *
   * @static
   *
   * @param {AccessCheckResponseRoleEnum} role The role to check.
   * @returns {boolean} true if a temp admin role.
   *
   * @memberof AccessChecker
   */
  public static isTempAdminRole(role: AccessCheckResponseRoleEnum): boolean {
    return role === AccessCheckResponseRoleEnum.TEMPADMIN;
  }
}
